import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import {
    ShieldCheck,
    ShieldAlert,
    AlertTriangle,
    ArrowRight,
    Loader2,
    Users,
    FileClock,
} from "lucide-react";

/**
 * Compact "Conformità HR" widget for the homepage Overview.
 *
 * Shows: consent rate · download rate · pending acks · top 5 most non-compliant employees.
 * Clicks navigate to /conformita for the full dashboard.
 */
export const ComplianceWidget = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let mounted = true;
        api.get("/delivery/compliance/overview", {
            params: { only_non_compliant: true },
        }).then((r) => {
            if (mounted) setData(r.data);
        }).catch(() => {
            if (mounted) setData(null);
        }).finally(() => {
            if (mounted) setLoading(false);
        });
        return () => { mounted = false; };
    }, []);

    if (loading) {
        return (
            <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="compliance-widget">
                <div className="flex h-40 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
            </section>
        );
    }

    if (!data) return null;

    const kpis = data.kpis || {};
    const topRows = (data.employees || []).slice(0, 8);
    const consentRate = kpis.consent_rate ?? 0;
    const downloadRate = kpis.download_rate ?? 0;
    const pendingAcks = kpis.pending_acks ?? 0;
    const nonCompliantCount = kpis.non_compliant_employees ?? 0;
    const noCredentials = kpis.no_credentials ?? 0;

    // Overall health: green if consent>=80 && downloads>=60 && pendingAcks==0
    const isHealthy = consentRate >= 80 && downloadRate >= 60 && pendingAcks === 0;
    const headerTone = isHealthy
        ? "border-emerald-200 bg-emerald-50"
        : nonCompliantCount > 10
            ? "border-red-200 bg-red-50"
            : "border-amber-200 bg-amber-50";
    const HeaderIcon = isHealthy ? ShieldCheck : ShieldAlert;
    const headerColor = isHealthy ? "text-emerald-700" : nonCompliantCount > 10 ? "text-red-700" : "text-amber-700";

    return (
        <section className="flex-1 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="compliance-widget">
            <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-display text-base font-semibold text-slate-700">
                    <ShieldCheck className="h-4 w-4 text-[#376DB3]" />
                    Conformità HR
                </h2>
                <Link
                    to="/conformita"
                    className="flex items-center gap-1 text-xs font-medium text-[#376DB3] hover:underline"
                    data-testid="compliance-widget-link"
                >
                    Vai alla dashboard
                    <ArrowRight className="h-3 w-3" />
                </Link>
            </div>

            {/* Overall status pill */}
            <div className={`mb-3 flex items-center gap-2 rounded-md border px-3 py-2 ${headerTone}`} data-testid="compliance-widget-status">
                <HeaderIcon className={`h-4 w-4 ${headerColor}`} />
                <span className={`text-sm font-medium ${headerColor}`}>
                    {isHealthy
                        ? "Conformità complessivamente OK"
                        : nonCompliantCount > 0
                            ? `${nonCompliantCount} dipendenti non conformi`
                            : "Verifica dati"}
                </span>
            </div>

            {/* Quick stats */}
            <div className="mb-4 grid grid-cols-3 gap-2 text-center text-xs">
                <Mini value={`${consentRate}%`} label="Consenso" tone={consentRate >= 80 ? "emerald" : consentRate >= 40 ? "amber" : "red"} testid="compliance-widget-consent" />
                <Mini value={`${downloadRate}%`} label="Download" tone={downloadRate >= 60 ? "emerald" : downloadRate >= 30 ? "amber" : "red"} testid="compliance-widget-download" />
                <Mini value={pendingAcks} label="Ack pendenti" tone={pendingAcks === 0 ? "emerald" : "amber"} testid="compliance-widget-acks" />
            </div>

            {/* Top non-compliant */}
            {topRows.length > 0 && (
                <div data-testid="compliance-widget-top-list">
                    <div className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        <AlertTriangle className="h-3 w-3 text-amber-500" />
                        Top non conformi
                    </div>
                    <div className="space-y-1.5">
                        {topRows.map((r) => (
                            <Link
                                key={r.employee_id}
                                to={`/dipendenti/${r.employee_id}`}
                                className="flex items-center justify-between rounded-md border border-slate-100 bg-slate-50/60 px-2.5 py-1.5 text-sm transition-colors hover:bg-slate-100"
                                data-testid={`compliance-widget-row-${r.employee_id}`}
                            >
                                <div className="min-w-0 flex-1">
                                    <div className="truncate font-medium text-slate-700">
                                        {r.cognome} {r.nome}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                        {!r.consent_given && <span className="font-medium text-amber-700">Consenso mancante · </span>}
                                        {r.pending_download > 0 && <span>{r.pending_download} doc da scaricare</span>}
                                        {r.pending_ack > 0 && <span className="ml-1 text-red-600">· {r.pending_ack} ack</span>}
                                    </div>
                                </div>
                                <ArrowRight className="h-3 w-3 flex-shrink-0 text-slate-400" />
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Extra hints */}
            {(noCredentials > 0 || topRows.length === 0) && (
                <div className="mt-3 grid gap-1.5 text-[11px] text-slate-500">
                    {noCredentials > 0 && (
                        <div className="flex items-center gap-1.5">
                            <Users className="h-3 w-3" />
                            <span>{noCredentials} dipendenti senza credenziali di accesso</span>
                        </div>
                    )}
                    {topRows.length === 0 && (
                        <div className="flex items-center gap-1.5 text-emerald-600">
                            <FileClock className="h-3 w-3" />
                            <span>Nessuna non-conformità rilevata</span>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

const TONE_CLASSES = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
};

const Mini = ({ value, label, tone, testid }) => (
    <div className={`rounded-md border px-2 py-1.5 ${TONE_CLASSES[tone]}`} data-testid={testid}>
        <div className="font-mono-numbers text-base font-bold">{value}</div>
        <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
    </div>
);
